/* eslint-disable @typescript-eslint/no-explicit-any */
import messages from '@/constants/messages';
import { ACCESS_TOKEN_KEY, ACCESS_TOKEN_MAX_AGE, HTTP_OK, HTTP_OK_2 } from '@/utils/constant';
import { setCookie } from '@/utils/cookies';
import { ApiErrorResponse, CommonResponse } from '@/utils/types/common';
import axios, { AxiosInstance } from 'axios';

/**
 * Configuration object for traditional API calls
 */
interface ApiCallConfig<TData = unknown, TParams = unknown> {
  api: AxiosInstance;
  method: 'get' | 'post' | 'put' | 'patch' | 'delete';
  endpoint: string;
  data?: TData;
  params?: TParams;
  acceptedStatuses?: number[];
  signal?: AbortSignal;
}

/**
 * Helper function for traditional API pattern
 * Handles all the repetitive error handling, token management, and response formatting
 *
 * @param config - Configuration object containing all API call parameters
 *
 * @example
 * // GET request
 * export const getCountryList = async (): Promise<GetCountryListResponse | ApiErrorResponse> => {
 *   return traditionalApiCall<GetCountryListResponse>({
 *     api: apiInventry,
 *     method: 'get',
 *     endpoint: '/country-codes'
 *   });
 * };
 *
 * // POST request
 * export const createCustomer = async (params: CreateCustomer): Promise<CreateCustomerResponse | ApiErrorResponse> => {
 *   return traditionalApiCall<CreateCustomerResponse>({
 *     api: apiInventry,
 *     method: 'post',
 *     endpoint: '/customer-information/create-customer',
 *     data: params
 *   });
 * };
 */
export async function traditionalApiCall<TResponse = CommonResponse, TData = unknown, TParams = unknown>(
  config: ApiCallConfig<TData, TParams>,
): Promise<TResponse | ApiErrorResponse> {
  const { api, method, endpoint, data, params, acceptedStatuses, signal } = config;

  try {
    // Determine default accepted status codes based on method
    const defaultStatuses = [HTTP_OK, HTTP_OK_2];
    const validStatuses = acceptedStatuses || defaultStatuses;

    // Make the API call based on method
    let response;
    switch (method) {
      case 'get':
        response = await api.get(endpoint, { params: params ?? data, signal });
        break;
      case 'post':
        response = await api.post(endpoint, data, { params, signal });
        break;
      case 'put':
        response = await api.put(endpoint, data, { params, signal });
        break;
      case 'patch':
        response = await api.patch(endpoint, data, { params, signal });
        break;
      case 'delete':
        response = await api.delete(endpoint, { data, params, signal });
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    const { data: responseData, status } = response;

    // Check if status is acceptable
    if (!validStatuses.includes(status)) {
      return {
        message: responseData?.message ?? messages.UNKNOWN_ERROR,
        status: false,
        data: responseData,
      } as ApiErrorResponse;
    }

    // Handle access token if present
    if (responseData?.accessToken != null) {
      await setCookie(ACCESS_TOKEN_KEY, responseData.accessToken, {
        maxAge: ACCESS_TOKEN_MAX_AGE,
        path: '/',
      });
    }

    return responseData as TResponse;
  } catch (error: unknown) {
    // Handle axios errors
    const message =
      axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : (error as Error).message || messages.UNKNOWN_ERROR;

    return {
      message,
      status: false,
      data: null,
    } as ApiErrorResponse;
  }
}

/**
 * Convenience wrappers for specific HTTP methods
 * These provide a cleaner API while using the main helper internally
 */

export async function traditionalGet<TResponse = any, TParams = unknown>(config: {
  api: AxiosInstance;
  endpoint: string;
  params?: TParams;
  acceptedStatuses?: number[];
  signal?: AbortSignal; // Add signal support
}): Promise<TResponse | ApiErrorResponse> {
  return traditionalApiCall<TResponse, undefined, TParams>({
    ...config,
    method: 'get',
    data: undefined,
  });
}

export async function traditionalPost<TResponse = any, TData = unknown>(config: {
  api: AxiosInstance;
  endpoint: string;
  data?: TData;
  acceptedStatuses?: number[];
}): Promise<TResponse | ApiErrorResponse> {
  return traditionalApiCall<TResponse, TData, undefined>({
    ...config,
    method: 'post',
    params: undefined,
  });
}

export async function traditionalPut<TResponse = any, TData = unknown, TParams = unknown>(config: {
  api: AxiosInstance;
  endpoint: string;
  data?: TData;
  params?: TParams;
  acceptedStatuses?: number[];
}): Promise<TResponse | ApiErrorResponse> {
  return traditionalApiCall<TResponse, TData, TParams>({
    ...config,
    method: 'put',
  });
}

export async function traditionalPatch<TResponse = any, TData = unknown>(config: {
  api: AxiosInstance;
  endpoint: string;
  data?: TData;
  acceptedStatuses?: number[];
}): Promise<TResponse | ApiErrorResponse> {
  return traditionalApiCall<TResponse, TData, undefined>({
    ...config,
    method: 'patch',
    params: undefined,
  });
}

export async function traditionalDelete<TResponse = any, TData = unknown>(config: {
  api: AxiosInstance;
  endpoint: string;
  data?: TData;
  acceptedStatuses?: number[];
}): Promise<TResponse | ApiErrorResponse> {
  return traditionalApiCall<TResponse, TData, undefined>({
    ...config,
    method: 'delete',
    params: undefined,
  });
}
